/* Collection "See more" — appends the next page of the grid in place of the
 * pagination, and brings the visitor back where they were when they return
 * from a product.
 *
 * The collection is still paginated by Liquid (48 per page): the button is a
 * link to the next page, so that without JavaScript, or when the fetch fails,
 * it simply opens that page. With JavaScript, a click fetches the same URL as
 * a section rendering (`section_id`), takes its cards and appends them to the
 * grid, then points the link at the page after — or removes it on the last
 * page. The URL of the window does not change.
 *
 * Back navigation: the appended pages live only in the DOM, so when the
 * visitor opens a product and comes back, the browser renders the collection
 * afresh with its first 48 products and cannot restore a scroll position
 * that lies below them — the page landed on the "See more" button. So the
 * component saves, when the page is left (`pagehide`), the number of pages
 * it appended and `scrollY`, in `sessionStorage` under the URL of the page
 * (path and query: a filtered page has its own key, the filters rewrite the
 * URL). On a back/forward navigation or a reload, it fetches those pages
 * again, all at once, appends them in order and scrolls to the saved
 * position — holding it while the page is still growing, see `settle`. An
 * inline script in
 * `sections/collection.liquid` sets `history.scrollRestoration` to `manual`
 * as soon as a saved state exists, so that the browser does not scroll on
 * its own first. The entry is consumed when read — a plain navigation to the
 * same URL (a menu link) just drops it — and a page restored from the
 * back/forward cache needs nothing: its DOM and position survived. When the
 * page has no "See more", nothing is saved and the browser restores the
 * position itself.
 *
 * Why a file of its own: same reason as `product-card.js` — `shopify theme dev`
 * truncates the compiled `scripts.js`, files in `assets/` are served whole.
 * `sections/collection.liquid` loads it.
 *
 * The appended cards need nothing more: `<product-card>` is defined by
 * `product-card.js`, already on the page, and upgrades on insertion.
 *
 * Markup contract:
 *
 *   <collection-load-more data-section-id="…" data-status="… [shown] … [total] …"
 *                         data-total="…">
 *     <p role="status" data-load-more-status></p>   announces the new count
 *     <a href="<next page>" data-load-more>…</a>
 *   </collection-load-more>
 *
 *   and, in the same `.collection`, the grid `.collection__grid` whose children
 *   `.collection__item` are the cards.
 */

/* One restoration per document: the filters replace the whole component on
   every selection, and the new instance must not restore again. */
let restored = false;

class CollectionLoadMore extends HTMLElement {
  connectedCallback() {
    this.link = this.querySelector('[data-load-more]');
    this.status = this.querySelector('[data-load-more-status]');
    this.grid = this.closest('.collection')?.querySelector('.collection__grid');
    if (!this.link || !this.grid) return;

    // Pages appended below the first one, saved for the return.
    this.pages = 0;

    this.onClick = this.onClick.bind(this);
    this.save = this.save.bind(this);
    this.onPageShow = this.onPageShow.bind(this);
    this.link.addEventListener('click', this.onClick);
    window.addEventListener('pagehide', this.save);
    window.addEventListener('pageshow', this.onPageShow);

    this.restore();
  }

  disconnectedCallback() {
    this.link?.removeEventListener('click', this.onClick);
    window.removeEventListener('pagehide', this.save);
    window.removeEventListener('pageshow', this.onPageShow);
  }

  onClick(event) {
    // A click meant to open a new tab or window keeps the link's behaviour.
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    event.preventDefault();
    this.load();
  }

  /* The URL of a page of this collection, as a section rendering. */
  sectionUrl(href, page) {
    const url = new URL(href, window.location.href);
    url.searchParams.set('section_id', this.dataset.sectionId);
    if (page) url.searchParams.set('page', page);
    return url;
  }

  /* Fetches a page and returns its cards and its own "See more" link (null on
     the last page). */
  async fetchPage(url) {
    const response = await fetch(url);
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const html = new DOMParser().parseFromString(await response.text(), 'text/html');

    const items = [...html.querySelectorAll('.collection__grid > .collection__item')];
    if (!items.length) throw new Error('No product in the response');
    return { items, next: html.querySelector('collection-load-more [data-load-more]') };
  }

  /* Appends the cards of a page and moves the link to the page after. */
  append({ items, next }) {
    this.grid.append(...items);
    this.pages += 1;
    if (next) {
      this.link.setAttribute('href', next.getAttribute('href'));
    } else {
      this.link.remove();
    }
  }

  async load() {
    if (this.loading) return;
    this.loading = true;
    this.link.setAttribute('aria-disabled', 'true');
    this.setAttribute('aria-busy', 'true');

    try {
      const page = await this.fetchPage(this.sectionUrl(this.link.href));
      this.append(page);
      this.announce();

      // Keyboard users go on from the first new card rather than from the top
      // of the page (the link may be gone). Without scrolling: the new cards
      // are right below the ones already seen.
      page.items[0].querySelector('a[href], button:not([disabled])')?.focus({ preventScroll: true });
    } catch (error) {
      // The page itself is always there: open it.
      console.error(error);
      window.location.href = this.link.href;
      return;
    } finally {
      this.loading = false;
      this.link.removeAttribute('aria-disabled');
      this.removeAttribute('aria-busy');
    }
  }

  announce() {
    if (!this.status || !this.dataset.status) return;
    const shown = this.grid.querySelectorAll(':scope > .collection__item').length;
    this.status.textContent = this.dataset.status
      .replace('[shown]', shown)
      .replace('[total]', this.dataset.total);
  }

  /* --- Return to the page ------------------------------------------------ */

  get storageKey() {
    return `collection:${window.location.pathname}${window.location.search}`;
  }

  /* On leaving the page: what to put back on return. The position is also
     kept in memory, for a return from the back/forward cache (see
     `onPageShow`). */
  save() {
    this.leftAt = window.scrollY;
    try {
      sessionStorage.setItem(this.storageKey, JSON.stringify({ pages: this.pages, scrollY: this.leftAt }));
    } catch (error) {
      // Storage unavailable: the browser restores what it can.
    }
  }

  /* A page back from the back/forward cache kept its DOM and, normally, its
     position. Should the browser have lost the latter, it is put back from
     the value kept at `pagehide`. */
  onPageShow(event) {
    if (!event.persisted || this.leftAt == null) return;
    this.settle(this.leftAt);
  }

  /* How the document was reached: `back_forward`, `reload`, `navigate`… */
  get navigationType() {
    const entry = performance.getEntriesByType?.('navigation')[0];
    if (entry) return entry.type;
    const legacy = performance.navigation?.type;
    return legacy === 2 ? 'back_forward' : legacy === 1 ? 'reload' : 'navigate';
  }

  /* On arrival: reads and drops the saved state, then, when the visitor came
     back (or reloaded), appends the pages they had loaded and scrolls where
     they were. */
  async restore() {
    if (restored) return;
    restored = true;

    let saved = null;
    try {
      saved = JSON.parse(sessionStorage.getItem(this.storageKey) || 'null');
      sessionStorage.removeItem(this.storageKey);
    } catch (error) {
      return;
    }
    if (!saved) return;

    const type = this.navigationType;
    if (type !== 'back_forward' && type !== 'reload') return;

    this.setAttribute('aria-busy', 'true');
    try {
      // The link points at page 2: the pages to put back are 2 to pages + 1.
      const urls = [];
      for (let page = 2; page <= saved.pages + 1; page += 1) urls.push(this.sectionUrl(this.link.href, page));
      const pages = await Promise.allSettled(urls.map((url) => this.fetchPage(url)));
      // In order, up to the first page that failed (the collection may have
      // shrunk): the position is then clamped to what is there.
      for (const page of pages) {
        if (page.status !== 'fulfilled') {
          console.error(page.reason);
          break;
        }
        this.append(page.value);
      }
    } finally {
      this.removeAttribute('aria-busy');
    }

    this.settle(saved.scrollY);
  }

  /* Scrolls to `top` and holds the position for a few seconds: a scroll
     beyond the current height is clamped, so it is repeated whenever the
     document grows (fonts, late images, late sections), and a scroll that
     the visitor did not ask for — a browser putting the page back to the top
     once loaded — is undone. It lets go at the first gesture of the visitor
     (wheel, touch, pointer, key) or after five seconds. A plain `scrollTo`
     with two arguments: the options form with `behavior` is rejected by some
     WebKit versions. */
  settle(top) {
    const reached = () => Math.abs(window.scrollY - top) < 2;
    // `short`: the document was too short at the last attempt, the scroll
    // was clamped, and a growth of the document is worth another one.
    let short = false;
    const apply = () => {
      if (reached()) return;
      window.scrollTo(0, top);
      short = !reached();
    };
    apply();

    const gestures = ['wheel', 'touchstart', 'pointerdown', 'mousedown', 'keydown'];
    // A scrollbar drag fires no gesture event and must not be fought: only a
    // return to the very top is undone, and a growth of the document only
    // matters while the position is out of reach.
    const onScroll = () => {
      if (window.scrollY < 1) apply();
    };
    const observer = new ResizeObserver(() => {
      if (short) apply();
    });
    const stop = () => {
      observer.disconnect();
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('load', apply);
      gestures.forEach((type) => window.removeEventListener(type, stop));
      clearTimeout(timer);
    };
    const timer = setTimeout(stop, 5000);
    gestures.forEach((type) => window.addEventListener(type, stop, { passive: true }));
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('load', apply);
    observer.observe(document.documentElement);
  }
}

if (!customElements.get('collection-load-more')) {
  customElements.define('collection-load-more', CollectionLoadMore);
}
